/**
 * Commerce Service for Payment Processing, Billing, and Subscription Management
 * Integrates with Stripe and handles all financial operations
 */

import { apiService, ApiResponse } from './api';

export interface PaymentMethod {
  id: string;
  type: 'card' | 'bank_account' | 'wallet';
  card?: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
    country: string;
  };
  bankAccount?: {
    bankName: string;
    accountType: string;
    last4: string;
    routingNumber: string;
  };
  wallet?: {
    type: string;
    email?: string;
  };
  isDefault: boolean;
  createdAt: string;
}

export interface BillingAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface PricingPlan {
  id: string;
  name: string;
  description: string;
  type: 'free' | 'premium' | 'enterprise';
  price: number;
  currency: string;
  billingCycle: 'monthly' | 'annual';
  features: string[];
  limits: {
    functionsPerMonth: number;
    storageGB: number;
    apiCallsPerMonth: number;
    supportLevel: 'community' | 'standard' | 'priority';
  };
  popular: boolean;
  trial: {
    available: boolean;
    durationDays: number;
  };
}

export interface Subscription {
  id: string;
  planId: string;
  plan: PricingPlan;
  status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trialing';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEnd?: string;
  cancelAtPeriodEnd: boolean;
  usage: {
    functions: number;
    storage: number;
    apiCalls: number;
    resetDate: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface Invoice {
  id: string;
  number: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  amount: number;
  currency: string;
  tax: number;
  total: number;
  dueDate: string;
  paidAt?: string;
  items: InvoiceItem[];
  paymentMethod?: PaymentMethod;
  downloadUrl?: string;
  createdAt: string;
}

export interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitAmount: number;
  amount: number;
  period?: {
    start: string;
    end: string;
  };
}

export interface PaymentIntent {
  id: string;
  clientSecret: string;
  amount: number;
  currency: string;
  status: 'requires_payment_method' | 'requires_confirmation' | 'processing' | 'succeeded' | 'canceled';
  paymentMethodId?: string;
}

export interface UsageRecord {
  id: string;
  subscriptionId: string;
  metricType: 'functions' | 'storage' | 'api_calls';
  quantity: number;
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface BillingPortalSession {
  url: string;
  returnUrl: string;
  expiresAt: string;
}

class CommerceService {
  private readonly basePath = '/api/v1/commerce';

  // Pricing Plans
  async getPricingPlans(): Promise<ApiResponse<PricingPlan[]>> {
    return apiService.get(`${this.basePath}/plans`);
  }

  async getPlan(planId: string): Promise<ApiResponse<PricingPlan>> {
    return apiService.get(`${this.basePath}/plans/${planId}`);
  }

  // Subscription Management
  async getCurrentSubscription(): Promise<ApiResponse<Subscription | null>> {
    return apiService.get(`${this.basePath}/subscription`);
  }

  async createSubscription(planId: string, paymentMethodId?: string, trialDays?: number): Promise<ApiResponse<{
    subscription: Subscription;
    paymentIntent?: PaymentIntent;
  }>> {
    return apiService.post(`${this.basePath}/subscription`, {
      planId,
      paymentMethodId,
      trialDays
    });
  }

  async updateSubscription(planId: string): Promise<ApiResponse<Subscription>> {
    return apiService.patch(`${this.basePath}/subscription`, { planId });
  }

  async cancelSubscription(immediate: boolean = false): Promise<ApiResponse<Subscription>> {
    return apiService.delete(`${this.basePath}/subscription?immediate=${immediate}`);
  }

  async reactivateSubscription(): Promise<ApiResponse<Subscription>> {
    return apiService.post(`${this.basePath}/subscription/reactivate`);
  }

  async getSubscriptionUsage(): Promise<ApiResponse<{
    current: UsageRecord[];
    limits: {
      functions: number;
      storage: number;
      apiCalls: number;
    };
    period: {
      start: string;
      end: string;
    };
  }>> {
    return apiService.get(`${this.basePath}/subscription/usage`);
  }

  // Payment Methods
  async getPaymentMethods(): Promise<ApiResponse<PaymentMethod[]>> {
    return apiService.get(`${this.basePath}/payment-methods`);
  }

  async addPaymentMethod(paymentMethodId: string): Promise<ApiResponse<PaymentMethod>> {
    return apiService.post(`${this.basePath}/payment-methods`, { paymentMethodId });
  }

  async updatePaymentMethod(paymentMethodId: string, billingAddress: BillingAddress): Promise<ApiResponse<PaymentMethod>> {
    return apiService.patch(`${this.basePath}/payment-methods/${paymentMethodId}`, { billingAddress });
  }

  async deletePaymentMethod(paymentMethodId: string): Promise<ApiResponse<void>> {
    return apiService.delete(`${this.basePath}/payment-methods/${paymentMethodId}`);
  }

  async setDefaultPaymentMethod(paymentMethodId: string): Promise<ApiResponse<void>> {
    return apiService.post(`${this.basePath}/payment-methods/${paymentMethodId}/default`);
  }

  // Payment Processing
  async createPaymentIntent(amount: number, currency: string = 'usd', metadata?: Record<string, string>): Promise<ApiResponse<PaymentIntent>> {
    return apiService.post(`${this.basePath}/payment-intents`, {
      amount,
      currency,
      metadata
    });
  }

  async confirmPaymentIntent(paymentIntentId: string, paymentMethodId: string): Promise<ApiResponse<PaymentIntent>> {
    return apiService.post(`${this.basePath}/payment-intents/${paymentIntentId}/confirm`, {
      paymentMethodId
    });
  }

  async getPaymentIntent(paymentIntentId: string): Promise<ApiResponse<PaymentIntent>> {
    return apiService.get(`${this.basePath}/payment-intents/${paymentIntentId}`);
  }

  // Invoicing
  async getInvoices(page: number = 1, limit: number = 20): Promise<ApiResponse<{
    invoices: Invoice[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
  }>> {
    return apiService.get(`${this.basePath}/invoices?page=${page}&limit=${limit}`);
  }

  async getInvoice(invoiceId: string): Promise<ApiResponse<Invoice>> {
    return apiService.get(`${this.basePath}/invoices/${invoiceId}`);
  }

  async downloadInvoice(invoiceId: string): Promise<ApiResponse<{ downloadUrl: string }>> {
    return apiService.get(`${this.basePath}/invoices/${invoiceId}/download`);
  }

  async payInvoice(invoiceId: string, paymentMethodId?: string): Promise<ApiResponse<Invoice>> {
    return apiService.post(`${this.basePath}/invoices/${invoiceId}/pay`, {
      paymentMethodId
    });
  }

  // Usage Tracking
  async recordUsage(metricType: 'functions' | 'storage' | 'api_calls', quantity: number, metadata?: Record<string, any>): Promise<ApiResponse<UsageRecord>> {
    return apiService.post(`${this.basePath}/usage`, {
      metricType,
      quantity,
      metadata
    });
  }

  async getUsageHistory(metricType?: string, days: number = 30): Promise<ApiResponse<{
    records: UsageRecord[];
    total: number;
    aggregated: {
      daily: { date: string; total: number }[];
      monthly: { month: string; total: number }[];
    };
  }>> {
    const params = new URLSearchParams();
    if (metricType) params.append('metric', metricType);
    params.append('days', days.toString());

    return apiService.get(`${this.basePath}/usage?${params.toString()}`);
  }

  // Billing Portal
  async createBillingPortalSession(returnUrl?: string): Promise<ApiResponse<BillingPortalSession>> {
    return apiService.post(`${this.basePath}/billing-portal`, {
      returnUrl: returnUrl || window.location.href
    });
  }

  // Tax and Compliance
  async updateTaxInformation(taxInfo: {
    taxId?: string;
    type: 'individual' | 'business';
    country: string;
    state?: string;
    businessName?: string;
  }): Promise<ApiResponse<void>> {
    return apiService.post(`${this.basePath}/tax-info`, taxInfo);
  }

  async getTaxInformation(): Promise<ApiResponse<{
    taxId?: string;
    type: 'individual' | 'business';
    country: string;
    state?: string;
    businessName?: string;
    vatRegistered: boolean;
  }>> {
    return apiService.get(`${this.basePath}/tax-info`);
  }

  // Webhooks for Real-time Updates
  async setupWebhook(url: string, events: string[]): Promise<ApiResponse<{
    id: string;
    url: string;
    events: string[];
    secret: string;
  }>> {
    return apiService.post(`${this.basePath}/webhooks`, { url, events });
  }

  async getWebhooks(): Promise<ApiResponse<Array<{
    id: string;
    url: string;
    events: string[];
    status: 'enabled' | 'disabled';
    createdAt: string;
  }>>> {
    return apiService.get(`${this.basePath}/webhooks`);
  }

  // Revenue Analytics (for function publishers)
  async getRevenueStats(period: '7d' | '30d' | '90d' | '1y' = '30d'): Promise<ApiResponse<{
    totalRevenue: number;
    revenueByDay: { date: string; revenue: number }[];
    topFunctions: Array<{
      functionId: string;
      name: string;
      revenue: number;
      sales: number;
    }>;
    payout: {
      available: number;
      pending: number;
      nextPayoutDate: string;
    };
  }>> {
    return apiService.get(`${this.basePath}/revenue?period=${period}`);
  }

  async requestPayout(amount?: number): Promise<ApiResponse<{
    payoutId: string;
    amount: number;
    estimatedArrival: string;
  }>> {
    return apiService.post(`${this.basePath}/payouts`, amount ? { amount } : undefined);
  }
}

export const commerceService = new CommerceService();
export default commerceService;